import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Clock, Copy, Pencil, Plus, RefreshCw, Trash2, UserX } from "lucide-react";
import { useState } from "react";

import type { Role } from "@/api/auth";
import {
  createAdmin,
  deactivateAdmin,
  deleteAdmin,
  getTempPassword,
  getUserLogs,
  getUserTotalTime,
  listAdmins,
  regeneratePassword,
  updateAdmin,
  type Admin,
  type LogoutReason,
  type SessionLogItem,
} from "@/api/users";
import { EmptyState } from "@/components/EmptyState";
import { Spinner } from "@/components/Spinner";
import { useAuth } from "@/auth/AuthProvider";
import {
  formatDateTime,
  formatDurationCompact,
  formatDurationHoursMinutes,
  formatTimeOnly,
  todayIstIso,
} from "@/lib/formatDate";
import { AdminHeader } from "@/pages/admin/components/AdminHeader";
import { useTabs } from "@/pages/admin/TabsContext";
import type { TabOut } from "@/api/tabs";

export function UsersPage() {
  const qc = useQueryClient();
  const { me } = useAuth();
  const [showCreate, setShowCreate] = useState(false);

  const list = useQuery({
    queryKey: ["admins"],
    queryFn: listAdmins,
  });

  const refreshAdmins = () => qc.invalidateQueries({ queryKey: ["admins"] });

  return (
    <div className="min-h-screen flex flex-col">
      <AdminHeader title="Manage Admin Users" backTo="/admin" />
      <main className="w-full max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex-1 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">Admin users</h2>
          <button
            type="button"
            className="btn-primary text-sm px-3 py-1.5 inline-flex items-center gap-1.5"
            onClick={() => setShowCreate(true)}
          >
            <Plus size={14} /> Add admin
          </button>
        </div>

        {list.isLoading ? (
          <div className="card p-12 flex justify-center">
            <Spinner label="Loading admins…" />
          </div>
        ) : list.isError ? (
          <div className="alert-danger">{(list.error as Error)?.message}</div>
        ) : list.data && list.data.items.length > 0 ? (
          <div className="card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 border-b border-canvas-border dark:border-surface-border">
                <tr>
                  <th className="w-8 px-2 py-2" aria-label="Expand logs" />
                  <th className="text-left px-4 py-2">Username</th>
                  <th className="text-left px-4 py-2">Full name</th>
                  <th className="text-left px-4 py-2">Role</th>
                  <th className="text-left px-4 py-2">Tab access</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">Last login</th>
                  <th className="text-right px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-canvas-border dark:divide-surface-border/70">
                {list.data.items.map((a) => (
                  <AdminRow
                    key={a.id}
                    admin={a}
                    isSelf={a.username === me?.username}
                    onChanged={refreshAdmins}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState title="No admins yet." description="Click 'Add admin' to create the first one." />
        )}
      </main>

      {showCreate && (
        <CreateAdminModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            refreshAdmins();
          }}
        />
      )}
    </div>
  );
}

function AdminRow({
  admin,
  isSelf,
  onChanged,
}: {
  admin: Admin;
  isSelf: boolean;
  onChanged: () => void;
}) {
  const [revealed, setRevealed] = useState<string | null>(null);
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied">("idle");
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const showTemp = useMutation({
    mutationFn: () => getTempPassword(admin.id),
    onSuccess: (r) => setRevealed(r.temp_password ?? "(none – user has changed their password)"),
    onError: (e: Error) => setError(e.message),
  });

  const regenerate = useMutation({
    mutationFn: () => regeneratePassword(admin.id),
    onSuccess: (r) => {
      setRevealed(r.temp_password ?? null);
      onChanged();
    },
    onError: (e: Error) => setError(e.message),
  });

  const deactivate = useMutation({
    mutationFn: () => deactivateAdmin(admin.id),
    onSuccess: () => onChanged(),
    onError: (e: Error) => setError(e.message),
  });

  const remove = useMutation({
    mutationFn: () => deleteAdmin(admin.id),
    onSuccess: () => onChanged(),
    onError: (e: Error) => setError(e.message),
  });

  async function copyToClipboard(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus("copied");
      setTimeout(() => setCopyStatus("idle"), 1500);
    } catch {
      setError("Clipboard copy failed. Select the text manually.");
    }
  }

  return (
    <>
    <tr>
      <td className="w-8 px-2 py-3 align-top">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="p-1 rounded-md text-slate-500 hover:bg-canvas-subtle dark:hover:bg-surface-border/60"
          aria-label={expanded ? "Hide login history" : "Show login history"}
          aria-expanded={expanded}
          title={expanded ? "Hide login history" : "Show login history"}
        >
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
      </td>
      <td className="px-4 py-3 font-mono text-slate-700 dark:text-slate-200">{admin.username}</td>
      <td className="px-4 py-3">{admin.full_name || <span className="text-slate-400">–</span>}</td>
      <td className="px-4 py-3">
        <span
          className={
            "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium " +
            (admin.role === "super_admin"
              ? "bg-brand-green/15 text-brand-green"
              : "bg-slate-200 text-slate-700 dark:bg-surface-border dark:text-slate-200")
          }
        >
          {admin.role === "super_admin" ? "Super admin" : "Admin"}
        </span>
      </td>
      <td className="px-4 py-3">
        <TabAccessBadge admin={admin} />
      </td>
      <td className="px-4 py-3">
        {admin.is_active ? (
          <span className="text-green-600 dark:text-green-400">Active</span>
        ) : (
          <span className="text-slate-500">Inactive</span>
        )}
        {admin.must_change_password && admin.is_active && (
          <span className="ml-2 text-[10px] whitespace-nowrap text-amber-600">
            must change password
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-xs text-slate-500">
        {admin.last_login ? formatDateTime(admin.last_login) : "never"}
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="flex items-center justify-end gap-1.5 flex-nowrap">
          {revealed !== null ? (
            <div className="inline-flex items-center gap-2 rounded-md border border-canvas-border dark:border-surface-border px-2 py-1">
              <code className="text-xs font-mono">{revealed}</code>
              <button
                type="button"
                className="btn-secondary text-xs px-2 py-0.5 inline-flex items-center gap-1"
                onClick={() => copyToClipboard(revealed)}
              >
                <Copy size={11} /> {copyStatus === "copied" ? "Copied" : "Copy"}
              </button>
              <button
                type="button"
                className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-200"
                onClick={() => setRevealed(null)}
              >
                Hide
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="btn-secondary text-xs px-2.5 py-1"
              disabled={!admin.is_active || showTemp.isPending}
              onClick={() => showTemp.mutate()}
            >
              Show temp password
            </button>
          )}
          <button
            type="button"
            className="btn-secondary text-xs px-2.5 py-1 inline-flex items-center gap-1"
            disabled={!admin.is_active || regenerate.isPending}
            onClick={() => regenerate.mutate()}
            title="Generate a fresh temporary password"
          >
            <RefreshCw size={11} /> Regenerate
          </button>
          <button
            type="button"
            className="text-xs px-2.5 py-1 rounded-md border border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-40 inline-flex items-center gap-1"
            disabled={!admin.is_active || isSelf || deactivate.isPending}
            onClick={() => {
              if (window.confirm(`Deactivate '${admin.username}'? They will no longer be able to log in.`)) {
                deactivate.mutate();
              }
            }}
            title={isSelf ? "You cannot deactivate yourself" : "Deactivate this admin"}
          >
            <UserX size={11} /> Deactivate
          </button>
          <button
            type="button"
            aria-label="Edit admin"
            title="Edit this admin"
            className="p-1.5 rounded-md border border-canvas-border dark:border-surface-border text-slate-600 dark:text-slate-300 hover:bg-canvas-subtle dark:hover:bg-surface-border/60 focus:outline-none focus:ring-2 focus:ring-brand-green/40"
            onClick={() => setEditing(true)}
          >
            <Pencil size={14} />
          </button>
          <button
            type="button"
            aria-label="Delete admin"
            title={isSelf ? "You cannot delete yourself" : "Permanently delete this admin"}
            className="p-1.5 rounded-md border border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-red-300"
            disabled={isSelf || remove.isPending}
            onClick={() => {
              if (
                window.confirm(
                  `Delete '${admin.username}'? This permanently removes the account and cannot be undone.`,
                )
              ) {
                remove.mutate();
              }
            }}
          >
            <Trash2 size={14} />
          </button>
        </div>
        {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
        {editing && (
          <EditAdminModal
            admin={admin}
            isSelf={isSelf}
            onClose={() => setEditing(false)}
            onSaved={() => {
              setEditing(false);
              onChanged();
            }}
          />
        )}
      </td>
    </tr>
    {expanded && (
      <tr className="bg-canvas-subtle/50 dark:bg-surface-border/20">
        <td colSpan={8} className="px-4 py-4">
          <UserLogsPanel adminId={admin.id} username={admin.username} />
        </td>
      </tr>
    )}
    </>
  );
}

function UserLogsPanel({ adminId, username }: { adminId: number; username: string }) {
  const [date, setDate] = useState<string>(() => todayIstIso());
  const [showTotal, setShowTotal] = useState(false);
  const isToday = date === todayIstIso();

  const q = useQuery({
    queryKey: ["admin-logs", adminId, date],
    queryFn: () => getUserLogs(adminId, date),
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-xs font-semibold text-slate-700 dark:text-slate-200">
          Login history · <span className="font-mono">{username}</span>
        </div>
        <div className="flex items-center gap-2 whitespace-nowrap">
          <label className="text-xs text-slate-500 whitespace-nowrap">Date (IST)</label>
          <input
            type="date"
            className="input-field text-xs px-2 py-1"
            value={date}
            onChange={(e) => setDate(e.target.value || todayIstIso())}
          />
          {!isToday && (
            <button
              type="button"
              className="btn-secondary text-xs px-2 py-1"
              onClick={() => setDate(todayIstIso())}
            >
              Today
            </button>
          )}
        </div>
        <button
          type="button"
          className="btn-secondary text-xs px-2 py-1 inline-flex items-center gap-1 whitespace-nowrap"
          onClick={() => setShowTotal((v) => !v)}
          aria-expanded={showTotal}
          title="Total time this admin has spent on the app, across all days"
        >
          <Clock size={11} />
          {showTotal ? "Hide total time" : "Total time on app"}
        </button>
        {q.data && (
          <div className="ml-auto text-xs text-slate-600 dark:text-slate-300">
            <span className="font-semibold">Total:</span>{" "}
            <span className="font-mono">{formatDurationCompact(q.data.total_seconds)}</span>
            {q.data.first_login && (
              <>
                {" · "}
                <span className="text-slate-500">First login</span>{" "}
                <span className="font-mono">{formatTimeOnly(q.data.first_login)}</span>
              </>
            )}
            {q.data.last_logout && (
              <>
                {" · "}
                <span className="text-slate-500">Last logout</span>{" "}
                <span className="font-mono">{formatTimeOnly(q.data.last_logout)}</span>
              </>
            )}
          </div>
        )}
      </div>

      {showTotal && <TotalTimePanel adminId={adminId} />}

      {q.isLoading ? (
        <div className="p-6 flex justify-center">
          <Spinner label="Loading logs…" />
        </div>
      ) : q.isError ? (
        <div className="alert-danger text-xs">{(q.error as Error)?.message}</div>
      ) : q.data && q.data.sessions.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400 border-b border-canvas-border dark:border-surface-border">
              <tr>
                <th className="text-left px-3 py-2">Login</th>
                <th className="text-left px-3 py-2">Logout</th>
                <th className="text-left px-3 py-2">Duration</th>
                <th className="text-left px-3 py-2">Reason</th>
                <th className="text-left px-3 py-2">IP</th>
                <th className="text-left px-3 py-2">User agent</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-canvas-border dark:divide-surface-border/70">
              {q.data.sessions.map((s) => (
                <SessionRow key={s.id} s={s} />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-xs text-slate-500 py-4">No login activity on this date.</div>
      )}
    </div>
  );
}

function TotalTimePanel({ adminId }: { adminId: number }) {
  const q = useQuery({
    queryKey: ["admin-total-time", adminId],
    queryFn: () => getUserTotalTime(adminId),
  });

  if (q.isLoading) {
    return (
      <div className="rounded-md border border-canvas-border dark:border-surface-border p-4 flex justify-center">
        <Spinner label="Loading total time…" />
      </div>
    );
  }
  if (q.isError) {
    return <div className="alert-danger text-xs">{(q.error as Error)?.message}</div>;
  }
  if (!q.data) return null;

  const { total_seconds, active_days, session_count, first_login, last_activity } = q.data;
  const avgPerDay = active_days > 0 ? Math.round(total_seconds / active_days) : 0;

  return (
    <div className="rounded-md border border-canvas-border dark:border-surface-border bg-canvas-subtle/60 dark:bg-surface-border/20 p-3">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Stat label="Total time on app" value={formatDurationHoursMinutes(total_seconds)} emphasis />
        <Stat label="Active days" value={String(active_days)} />
        <Stat label="Avg / active day" value={formatDurationHoursMinutes(avgPerDay)} />
        <Stat label="Sessions" value={String(session_count)} />
        <Stat
          label="First login"
          value={first_login ? formatDateTime(first_login) : "never"}
        />
      </div>
      <p className="mt-2 text-[10px] text-slate-500">
        Across every recorded session, including the one currently open
        {last_activity && <> · last activity {formatDateTime(last_activity)}</>}.
      </p>
    </div>
  );
}

function Stat({
  label,
  value,
  emphasis = false,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
        {label}
      </div>
      <div
        className={
          "font-mono " +
          (emphasis
            ? "text-sm font-semibold text-brand-green"
            : "text-xs text-slate-700 dark:text-slate-200")
        }
      >
        {value}
      </div>
    </div>
  );
}

function SessionRow({ s }: { s: SessionLogItem }) {
  return (
    <tr>
      <td className="px-3 py-2 font-mono">{formatDateTime(s.login_at)}</td>
      <td className="px-3 py-2 font-mono">
        {s.logout_at ? formatDateTime(s.logout_at) : <span className="text-emerald-600">still open</span>}
      </td>
      <td className="px-3 py-2 font-mono">{formatDurationCompact(s.duration_seconds)}</td>
      <td className="px-3 py-2">
        <ReasonBadge reason={s.logout_reason} />
      </td>
      <td className="px-3 py-2 font-mono text-slate-500">{s.ip_address ?? "–"}</td>
      <td className="px-3 py-2 text-slate-500 max-w-[16rem] truncate" title={s.user_agent ?? ""}>
        {s.user_agent ?? "–"}
      </td>
    </tr>
  );
}

function ReasonBadge({ reason }: { reason: LogoutReason | null }) {
  if (reason === null) {
    return (
      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
        active
      </span>
    );
  }
  const map: Record<LogoutReason, { label: string; cls: string }> = {
    manual: { label: "manual", cls: "bg-slate-200 text-slate-700 dark:bg-surface-border dark:text-slate-200" },
    inactivity: { label: "inactivity", cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300" },
    heartbeat_timeout: { label: "tab closed", cls: "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300" },
    superseded: { label: "superseded", cls: "bg-slate-100 text-slate-500 dark:bg-surface-border/60 dark:text-slate-400" },
  };
  const cfg = map[reason];
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

function TabAccessBadge({ admin }: { admin: Admin }) {
  const { tabs } = useTabs();

  if (admin.role === "super_admin") {
    return (
      <span
        className="inline-flex items-center rounded-full px-2 py-0.5 text-xs bg-slate-200 text-slate-700 dark:bg-surface-border dark:text-slate-200"
        title="Super admins have access to every tab."
      >
        All tabs
      </span>
    );
  }
  if (admin.allowed_tab_ids === null) {
    return (
      <span
        className="inline-flex items-center rounded-full px-2 py-0.5 text-xs bg-brand-green/15 text-brand-green"
        title="Includes tabs added in the future."
      >
        All tabs
      </span>
    );
  }
  if (admin.allowed_tab_ids.length === 0) {
    return (
      <span
        className="inline-flex items-center rounded-full px-2 py-0.5 text-xs bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300"
        title="This admin cannot see any tab."
      >
        No tabs
      </span>
    );
  }
  const names = admin.allowed_tab_ids.map(
    (id) => tabs.find((t) => t.id === id)?.name ?? id.slice(0, 6),
  );
  if (names.length <= 3) {
    return (
      <div className="flex flex-wrap gap-1">
        {names.map((n, i) => (
          <span
            key={i}
            className="inline-flex items-center rounded-full px-2 py-0.5 text-xs bg-slate-100 text-slate-700 dark:bg-surface-border/60 dark:text-slate-200"
          >
            {n}
          </span>
        ))}
      </div>
    );
  }
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-xs bg-slate-100 text-slate-700 dark:bg-surface-border/60 dark:text-slate-200"
      title={names.join(", ")}
    >
      {names.length} tabs
    </span>
  );
}

function CreateAdminModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [username, setUsername] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("admin");
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied">("idle");

  const create = useMutation({
    mutationFn: () =>
      createAdmin({
        username: username.trim(),
        role,
        email: email.trim() || undefined,
        full_name: fullName.trim() || undefined,
      }),
    onSuccess: (r) => {
      setTempPassword(r.temp_password);
      setError(null);
      onCreated();
    },
    onError: (e: Error) => setError(e.message),
  });

  async function copyPw() {
    if (!tempPassword) return;
    try {
      await navigator.clipboard.writeText(tempPassword);
      setCopyStatus("copied");
      setTimeout(() => setCopyStatus("idle"), 1500);
    } catch {
      /* clipboard blocked; leave visible */
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-lg card p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold">Add admin</h3>
          <button type="button" className="text-sm text-slate-500 hover:text-slate-700" onClick={onClose}>
            Close
          </button>
        </div>

        {tempPassword ? (
          <div className="space-y-3">
            <p className="text-sm">
              Account <strong>{username}</strong> created. Share the temporary password with them:
            </p>
            <div className="flex items-center gap-2 rounded-md border border-canvas-border dark:border-surface-border p-2">
              <code className="flex-1 font-mono text-sm break-all">{tempPassword}</code>
              <button type="button" className="btn-secondary text-xs px-2 py-1 inline-flex items-center gap-1" onClick={copyPw}>
                <Copy size={11} /> {copyStatus === "copied" ? "Copied" : "Copy"}
              </button>
            </div>
            <p className="text-xs text-slate-500">
              You can view or copy this password later from the admin list, until the user logs in
              and sets their own.
            </p>
            <div className="flex justify-end">
              <button type="button" className="btn-primary text-sm px-3 py-1.5" onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (!username.trim()) {
                setError("Username is required.");
                return;
              }
              create.mutate();
            }}
          >
            <div className="space-y-1">
              <label className="block text-xs uppercase tracking-wide text-slate-500">Username</label>
              <input
                className="input-field"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoFocus
              />
            </div>
            <div className="space-y-1">
              <label className="block text-xs uppercase tracking-wide text-slate-500">Full name (optional)</label>
              <input
                className="input-field"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="block text-xs uppercase tracking-wide text-slate-500">Email (optional)</label>
              <input
                type="email"
                className="input-field"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="block text-xs uppercase tracking-wide text-slate-500">Role</label>
              <select
                className="input-field text-xs"
                value={role}
                onChange={(e) => setRole(e.target.value as Role)}
              >
                <option value="admin" className="text-xs">Admin</option>
                <option value="super_admin" className="text-xs">Super admin</option>
              </select>
            </div>
            {error && <div className="alert-danger text-xs">{error}</div>}
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-secondary text-sm px-3 py-1.5" onClick={onClose}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary text-sm px-3 py-1.5"
                disabled={create.isPending}
              >
                {create.isPending ? "Creating…" : "Create admin"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function EditAdminModal({
  admin,
  isSelf,
  onClose,
  onSaved,
}: {
  admin: Admin;
  isSelf: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { tabs } = useTabs();
  const [fullName, setFullName] = useState(admin.full_name ?? "");
  const [email, setEmail] = useState(admin.email ?? "");
  const [role, setRole] = useState<Role>(admin.role);
  // Tab-access UI state. `allTabs=true` → send null; false → send picked set.
  const [allTabs, setAllTabs] = useState<boolean>(admin.allowed_tab_ids === null);
  const [picked, setPicked] = useState<Set<string>>(
    () => new Set(admin.allowed_tab_ids ?? []),
  );
  const [tabAccessTouched, setTabAccessTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const superAdmin = role === "super_admin";

  const save = useMutation({
    mutationFn: () => {
      const body: Parameters<typeof updateAdmin>[1] = {
        full_name: fullName.trim() || null,
        email: email.trim() || null,
        role,
      };
      if (tabAccessTouched && !superAdmin) {
        body.allowed_tab_ids = allTabs ? null : Array.from(picked);
      }
      return updateAdmin(admin.id, body);
    },
    onSuccess: () => onSaved(),
    onError: (e: Error) => setError(e.message),
  });

  function toggleTab(id: string) {
    setTabAccessTouched(true);
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-lg card p-6 space-y-4 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold">Edit admin</h3>
          <button type="button" className="text-sm text-slate-500 hover:text-slate-700" onClick={onClose}>
            Close
          </button>
        </div>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <div className="space-y-1">
            <label className="block text-xs uppercase tracking-wide text-slate-500">Username</label>
            <input className="input-field" value={admin.username} disabled />
          </div>
          <div className="space-y-1">
            <label className="block text-xs uppercase tracking-wide text-slate-500">Full name</label>
            <input
              className="input-field"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              autoFocus
            />
          </div>
          <div className="space-y-1">
            <label className="block text-xs uppercase tracking-wide text-slate-500">Email</label>
            <input
              type="email"
              className="input-field"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <label className="block text-xs uppercase tracking-wide text-slate-500">Role</label>
            <select
              className="input-field text-xs disabled:opacity-60"
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
              disabled={isSelf}
              title={isSelf ? "You cannot change your own role" : undefined}
            >
              <option value="admin" className="text-xs">Admin</option>
              <option value="super_admin" className="text-xs">Super admin</option>
            </select>
          </div>

          <div className="space-y-2 rounded-md border border-canvas-border dark:border-surface-border p-3">
            <label className="block text-xs uppercase tracking-wide text-slate-500">
              Tab access
            </label>
            {superAdmin ? (
              <p className="text-xs text-slate-500">
                Super admins have access to every tab by default.
              </p>
            ) : (
              <>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={allTabs}
                    onChange={(e) => {
                      setAllTabs(e.target.checked);
                      setTabAccessTouched(true);
                    }}
                  />
                  <span>Give access to all tabs (including future ones)</span>
                </label>
                {!allTabs && (
                  <TabPickerList
                    tabs={tabs}
                    picked={picked}
                    onToggle={toggleTab}
                  />
                )}
              </>
            )}
          </div>

          {error && <div className="alert-danger text-xs">{error}</div>}
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary text-sm px-3 py-1.5" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary text-sm px-3 py-1.5"
              disabled={save.isPending}
            >
              {save.isPending ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TabPickerList({
  tabs,
  picked,
  onToggle,
}: {
  tabs: TabOut[];
  picked: Set<string>;
  onToggle: (id: string) => void;
}) {
  if (tabs.length === 0) {
    return (
      <p className="text-xs text-slate-500">
        No tabs exist yet. Create tabs first to grant per-tab access.
      </p>
    );
  }
  return (
    <div className="max-h-56 overflow-y-auto rounded-md border border-canvas-border dark:border-surface-border divide-y divide-canvas-border dark:divide-surface-border/70">
      {tabs.map((t) => (
        <label
          key={t.id}
          className="flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-canvas-subtle dark:hover:bg-surface-border/60 cursor-pointer"
        >
          <input
            type="checkbox"
            checked={picked.has(t.id)}
            onChange={() => onToggle(t.id)}
          />
          <span className="flex-1">{t.name}</span>
          <span className="text-xs font-mono text-slate-400">{t.slug}</span>
        </label>
      ))}
    </div>
  );
}
